import React, { type ReactNode, useCallback, useRef } from 'react';
import {
  Select as SelectRAC,
  Button as ButtonRAC,
  SelectValue as SelectValueRAC,
  Popover as PopoverRAC,
  ListBox as ListBoxRAC,
  ListBoxItem as ListBoxItemRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';

import { type DropdownFieldProps } from './dropdown-field-props';

export type DropdownProps = {
  children: ReactNode;
};

export const Dropdown = ({ children }: DropdownProps) => {
  const {
    id,
    value,
    onChange,
    placeholder,
    disabled,
    required,
    onBlur,
    onFocus,
    FieldLayout,
    hasError,
  } = useFieldPropsV2<DropdownFieldProps>();

  const handleChange = useCallback(
    (selectedValue: React.Key | null) => {
      onChange(typeof selectedValue === 'string' ? selectedValue : null);
    },
    [onChange],
  );

  const labelElement = findChildOfType(children, Dropdown.Label);
  const triggerElement = findChildOfType(children, Dropdown.Trigger);
  const optionsElement = findChildOfType(children, Dropdown.Options);
  const descriptionElement = findChildOfType(children, Dropdown.Description);
  const errorElement = findChildOfType(children, Dropdown.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => (
        <SelectRAC
          value={value ?? null}
          onChange={handleChange}
          isDisabled={disabled}
          isInvalid={hasError}
          isRequired={required}
          placeholder={placeholder}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {triggerElement}
          {optionsElement}
        </SelectRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type TriggerProps = {
  className?: string;
};

const Trigger = ({ className }: TriggerProps) => {
  const { inputId, ariaDescribedBy } = useFieldAttributes();
  const { id, hasError } = useFieldPropsV2<DropdownFieldProps>();

  const ref = useRef<HTMLButtonElement>(null);
  useFocusFieldEvent(() => {
    ref.current?.focus();
  }, id);

  return (
    <ButtonRAC
      ref={ref}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
      data-invalid={hasError}
    >
      <SelectValueRAC />
    </ButtonRAC>
  );
};

type OptionsProps = {
  children: ReactNode;
  className?: string;
};

const Options = ({ children, className }: OptionsProps) => {
  const { options } = useFieldPropsV2<DropdownFieldProps>();

  const optionElement = findChildOfType(children, Options.Option);

  return (
    <PopoverRAC className={className}>
      <ListBoxRAC>
        {options.map((option) =>
          applyInternalProps<OptionInternalProps>(optionElement, {
            key: option.value,
            value: option.value,
            label: option.label,
          }),
        )}
      </ListBoxRAC>
    </PopoverRAC>
  );
};

type OptionProps = {
  className?: string;
};

type OptionInternalProps = {
  value: string;
  label: string;
};

const Option = ({ className, ...internal }: OptionProps) => {
  const { value, label } = internal as OptionInternalProps;
  return (
    <ListBoxItemRAC className={className} id={value} textValue={label}>
      {label}
    </ListBoxItemRAC>
  );
};

Options.Option = Option;

Dropdown.Label = Label;
Dropdown.Trigger = Trigger;
Dropdown.Options = Options;
Dropdown.Description = Description;
Dropdown.Error = Error;
